import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Droplets, Flame, PackageCheck, ShieldCheck, Sparkles, Truck } from "lucide-react";
import { formatINR } from "../lib/api";

const SAFETY_POINTS = [
  { icon: ShieldCheck, title: "Adults only", text: "Every burst is lit by an adult — keep the little ones cheering, not handling." },
  { icon: Flame, title: "Open spaces", text: "Light crackers outdoors, away from vehicles, wires and dry leaves." },
  { icon: Droplets, title: "Water ready", text: "A bucket of water or sand nearby finishes every show safely." },
  { icon: PackageCheck, title: "Store right", text: "Boxes stay sealed and cool until showtime — never in your pocket." },
];

const SOUND_LEVELS = [
  { level: 1, label: "Whisper", desc: "Sparklers & fountains" },
  { level: 2, label: "Crisp", desc: "Crackling chakkars" },
  { level: 3, label: "Cheer", desc: "Whistling rockets" },
  { level: 4, label: "Thunder", desc: "Lakshmi garlands" },
  { level: 5, label: "Skyfall", desc: "Atom & hydro bombs" },
];

const STEPS = [
  { icon: Sparkles, title: "Order placed", text: "Pick your crackers, place the COD order in a minute." },
  { icon: PackageCheck, title: "Hand-packed", text: "We pack your box in safety-grade wrap." },
  { icon: Truck, title: "Dispatched", text: "Tracked transport straight from our shop." },
  { icon: Flame, title: "Doorstep", text: "Celebrate the same week — festival-ready." },
];

export default function Safety() {
  const burstRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: burstRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="safety" className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[11px] tracking-[0.34em] uppercase text-ember">Burst responsibly</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-starlight">
            Celebrate safe, celebrate bright
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 relative overflow-hidden rounded-3xl border border-starlight/10 min-h-[420px]"
            ref={burstRef}
          >
            <motion.img
              src="/images/night-burst.jpeg"
              alt="Golden and crimson fireworks over a night sky"
              style={{ y: imgY }}
              className="absolute inset-0 h-[116%] w-full object-cover"
              data-testid="safety-parallax-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-transparent" />
            <div className="relative z-10 flex h-full flex-col justify-end p-6 sm:p-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                {SAFETY_POINTS.map((pt, i) => (
                  <motion.div
                    key={pt.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.55, delay: i * 0.1 }}
                    className="rounded-2xl border border-starlight/10 bg-obsidian/70 backdrop-blur p-4"
                  >
                    <pt.icon className="h-5 w-5 text-ember" />
                    <p className="mt-2.5 font-semibold text-starlight text-sm">{pt.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-night">{pt.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-4 rounded-3xl border border-starlight/10 bg-card p-6 sm:p-8 flex flex-col"
            data-testid="sound-scale-card"
          >
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-ember">Bang scale</p>
            <h3 className="mt-3 font-display text-2xl font-bold text-starlight">Pick your volume</h3>
            <p className="mt-2 text-sm text-muted-night">
              Every product card carries a bang rating from whisper-quiet to thunder-loud.
            </p>
            <div className="mt-6 space-y-3">
              {SOUND_LEVELS.map((s) => (
                <div key={s.level} className="flex items-center gap-3">
                  <span className="font-mono text-[10px] w-4 text-muted-night">{s.level}</span>
                  <div className="flex flex-1 gap-1">
                    {[1, 2, 3, 4, 5].map((b) => (
                      <span
                        key={b}
                        className={`h-2 flex-1 rounded-sm ${b <= s.level ? "bg-gradient-to-r from-crimson to-ember" : "bg-starlight/10"}`}
                      />
                    ))}
                  </div>
                  <div className="w-28 text-right">
                    <p className="text-xs font-semibold text-starlight leading-none">{s.label}</p>
                    <p className="font-mono text-[9px] text-muted-night mt-0.5">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-4 relative overflow-hidden rounded-3xl border border-starlight/10 min-h-[280px]"
          >
            <img
              src="/images/shop-display.jpeg"
              alt="Inside our fireworks shop with stacked cracker boxes"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-transparent" />
            <div className="relative z-10 flex h-full flex-col justify-end p-6 sm:p-8">
              <span className="inline-flex w-fit items-center rounded-full border border-ember/40 bg-obsidian/80 px-3 py-1 font-mono text-[10px] tracking-[0.24em] uppercase text-ember">
                From our shop shelf
              </span>
              <p className="mt-3 font-editorial italic text-xl text-starlight leading-snug">
                "Every box we sell is a box we'd light at our own home."
              </p>
              <p className="mt-2 text-xs text-muted-night">Fresh manufactured stock, never old inventory.</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-8 rounded-3xl border border-starlight/10 bg-card p-6 sm:p-8"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-ember">Doorstep express</p>
              <span className="rounded-full border border-discount-green/50 bg-discount-green/10 px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-discount-green" data-testid="cod-badge">
                Cash on delivery
              </span>
            </div>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6" id="delivery">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-ember/40 bg-ember/10">
                      <s.icon className="h-4.5 w-4.5 text-ember" />
                    </span>
                    <span className="font-mono text-2xl font-bold text-starlight/20">0{i + 1}</span>
                  </div>
                  <p className="mt-3 font-semibold text-starlight text-sm">{s.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-night">{s.text}</p>
                </motion.div>
              ))}
            </div>
            <p className="mt-8 border-t border-starlight/10 pt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-muted-night">
              Home delivery across your city & nearby towns • Free packing on orders above {formatINR(2000)}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
